import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { BasicFacility, TimeCardDetail, TimeCardShift } from '../../../../../js/api';
import { AssignmentsService, ConnectionsService } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { formatDuration } from '../../../utils/date';
import { TIMECARD_QUERY_KEY } from './queryKeys';
import { ShiftDocumentsModal } from './ShiftDocumentsModal';
import { ShiftEditModal } from './ShiftEditModal';
import { ShiftHistoryModal } from './ShiftHistoryModal';

const DATE_FORMAT = 'LLL d';

export const ShiftsTab: React.FC<{
  timecard: TimeCardDetail;
  canEdit?: boolean;
  onSuccess?: () => void;
}> = ({ timecard, canEdit = false, onSuccess }) => {
  const queryClient = useQueryClient();
  const [editingShift, setEditingShift] = useState<TimeCardShift | undefined>();
  const [historyShift, setHistoryShift] = useState<TimeCardShift | undefined>();
  const [documentsShift, setDocumentsShift] = useState<TimeCardShift | undefined>();

  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '--' });

  // Fetch connected facilities for facility dropdown
  const effectiveProviderId = timecard.provider?.id;
  const { data: connectedFacilitiesData } = useQuery({
    queryKey: ['timecardFacilities', effectiveProviderId],
    queryFn: async () => {
      const response = await ConnectionsService.connectionsConnectedFacilityList({
        provider: effectiveProviderId,
        status: ['ACTIVE', 'AWAIT_PRVLG'] as any,
        limit: 100,
      });
      return response.results.map((cf) => ({
        id: cf.facility.id,
        name: cf.facility.name,
        city: cf.facility.city,
        state: cf.facility.state,
      }));
    },
    enabled: !!effectiveProviderId && canEdit,
  });

  const facilityOptions = useMemo(() => {
    if (!connectedFacilitiesData) return [];
    return connectedFacilitiesData
      .filter((f) => f.id)
      .map((f) => ({
        value: f.id!,
        label: f.name || 'Unnamed Facility',
        facility: f as BasicFacility,
      }));
  }, [connectedFacilitiesData]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [TIMECARD_QUERY_KEY, timecard.id] });
    onSuccess?.();
  };

  const editMutation = useMutation({
    mutationFn: ({ shiftId, data }: { shiftId: string; data: Record<string, any> }) =>
      AssignmentsService.assignmentsTimecardShiftPartialUpdate({
        id: shiftId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Shift updated');
      setEditingShift(undefined);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const removeMutation = useMutation({
    mutationFn: ({
      shiftId,
      data,
    }: {
      shiftId: string;
      data: { removal_reason: string; explanation?: string };
    }) =>
      AssignmentsService.assignmentsTimecardShiftRemovePartialUpdate({
        id: shiftId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Shift removed');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const restoreMutation = useMutation({
    mutationFn: (shiftId: string) =>
      AssignmentsService.assignmentsTimecardShiftRestorePartialUpdate({ id: shiftId }),
    onSuccess: () => {
      toast.success('Shift restored');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const handleRemove = (shift: TimeCardShift) => {
    if (window.confirm(`Remove shift on ${shift.start_on}?`)) {
      removeMutation.mutate({
        shiftId: shift.id,
        data: { removal_reason: 'OTHER', explanation: '' },
      });
    }
  };

  const handleRestore = (shift: TimeCardShift) => {
    restoreMutation.mutate(shift.id);
  };

  const shifts = timecard.shifts || [];

  return (
    <div className="tw:space-y-4">
      {shifts.length === 0 ? (
        <div className="tw:text-center tw:text-zinc-500 tw:py-8">No shifts on this timecard</div>
      ) : (
        <Table compact striped>
          <TableHead>
            <TableHeader>
              <span />
            </TableHeader>
            <TableHeader>Date</TableHeader>
            <TableHeader>Times</TableHeader>
            <TableHeader>Duration</TableHeader>
            <TableHeader>Facility</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader className="tw:!text-right">Amount</TableHeader>
          </TableHead>
          <TableBody>
            {shifts.map((shift) => {
              const isRemoved = shift.is_removed;
              const modBadges: string[] = [];
              if (shift.is_added) modBadges.push('Added');
              if (shift.is_facility_changed) modBadges.push('Facility Changed');
              if (shift.is_extended) modBadges.push('Extended');
              if (shift.is_reduced) modBadges.push('Reduced');

              return (
                <TableRow
                  key={shift.id}
                  className={clsx(isRemoved && 'tw:opacity-50 tw:line-through')}
                >
                  <TableCell>
                    <div className="tw:flex tw:items-center tw:gap-2">
                      <button
                        type="button"
                        className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                        onClick={() => setHistoryShift(shift)}
                        title="View shift history"
                      >
                        <i className="fas fa-clock-rotate-left tw:text-sm" />
                      </button>
                      <button
                        type="button"
                        className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0 tw:relative"
                        onClick={() => setDocumentsShift(shift)}
                        title="Shift documents"
                      >
                        <i className="fas fa-paperclip tw:text-sm" />
                        {(shift.document_count ?? 0) > 0 && (
                          <span className="tw:absolute tw:-top-1.5 tw:-right-2 tw:bg-brand-green-0 tw:text-white tw:text-[9px] tw:leading-none tw:font-medium tw:rounded-full tw:min-w-[14px] tw:h-[14px] tw:flex tw:items-center tw:justify-center tw:px-0.5">
                            {shift.document_count}
                          </span>
                        )}
                      </button>
                      {canEdit && !isRemoved && (
                        <>
                          <button
                            type="button"
                            className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                            onClick={() => setEditingShift(shift)}
                            title="Edit shift"
                          >
                            <i className="fas fa-pencil tw:text-sm" />
                          </button>
                          <button
                            type="button"
                            className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                            onClick={() => handleRemove(shift)}
                            title="Remove shift"
                          >
                            <i className="fas fa-xmark tw:text-sm" />
                          </button>
                        </>
                      )}
                      {canEdit && isRemoved && (
                        <button
                          type="button"
                          className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                          onClick={() => handleRestore(shift)}
                          title="Restore shift"
                        >
                          <i className="fas fa-rotate-left tw:text-sm" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={clsx(isRemoved && 'tw:inline-block tw:[text-decoration:none]')}
                    >
                      {shift.start_on ? format(parseISO(shift.start_on), DATE_FORMAT) : '--'}
                    </span>
                    {modBadges.length > 0 && (
                      <div className="tw:flex tw:gap-1 tw:mt-0.5">
                        {modBadges.map((badge) => (
                          <span
                            key={badge}
                            className="tw:text-[9px] tw:bg-amber-100 tw:text-amber-700 tw:px-1 tw:rounded"
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {shift.start_time?.slice(0, 5)} – {shift.end_time?.slice(0, 5)}
                  </TableCell>
                  <TableCell>{shift.duration ? formatDuration(shift.duration) : '--'}</TableCell>
                  <TableCell>{shift.facility?.name || '--'}</TableCell>
                  <TableCell className="tw:text-xs">{shift.shift_rate_type}</TableCell>
                  <TableCell className="tw:text-right">
                    {formatCurrencyPostfix(shift.total_amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ShiftEditModal
        open={!!editingShift}
        onClose={() => setEditingShift(undefined)}
        onSave={(shiftId, data) => editMutation.mutate({ shiftId, data })}
        shift={editingShift}
        facilityOptions={facilityOptions}
        isPending={editMutation.isLoading}
      />

      <ShiftHistoryModal
        open={!!historyShift}
        onClose={() => setHistoryShift(undefined)}
        shift={historyShift}
      />

      <ShiftDocumentsModal
        open={!!documentsShift}
        onClose={() => {
          setDocumentsShift(undefined);
          invalidate();
        }}
        shift={documentsShift}
        ownerId={timecard.provider?.id || ''}
        relatedProviderId={timecard.provider?.id}
      />
    </div>
  );
};
